import { Search, X } from "lucide-react";
import type { ReactNode } from "react";
import { Dropdown, type DropdownOption } from "./Dropdown";
import { ViewModeToggle } from "./DataTable";
import { RowsPerCardSelect } from "./EntityCardGrid";

/* ── Filter definition ────────────────────────────────────
 * One entry per dropdown. Pass as many as the page needs — the bar wraps
 * rather than overflowing, so the count never breaks the layout. */
export interface ToolbarFilter {
  /** Stable identity for this filter (also used as the React key). */
  key: string;
  /** Currently selected value. Use `""` for the "all" option. */
  value: string;
  onChange: (value: string) => void;
  options: DropdownOption[];
  /** Shown when `value` is empty, e.g. "All Status". */
  placeholder?: string;
  /** Small label chip inside the trigger, before the value. */
  leadingLabel?: string;
  /** Adds a search box to the option list — for long lists. */
  searchable?: boolean;
  disabled?: boolean;
  /** Extra classes for this trigger, e.g. a wider `min-w-[200px]`. */
  className?: string;
  /** Skip rendering without disturbing the array (RBAC, conditional filters). */
  hidden?: boolean;
}

/* ── Search box ───────────────────────────────────────── */
export interface ToolbarSearch {
  value: string;
  onChange: (value: string) => void;
  /**
   * Called on Enter, on the search button, and when the box is cleared.
   * Omit to make the box purely controlled (filtering as you type).
   */
  onSubmit?: (value: string) => void;
  placeholder?: string;
  /** Every input needs a ceiling — defaults to 200 characters. */
  maxLength?: number;
  /** Accessible name for the input and its button. */
  ariaLabel?: string;
  disabled?: boolean;
}

export interface TableToolbarProps {
  /** Omit to hide the search box. */
  search?: ToolbarSearch;
  /** Filter dropdowns, rendered in order. Any length, including none. */
  filters?: ToolbarFilter[];

  /* ── Right-hand cluster (each part optional) ── */
  /** Card/Table toggle. Needs `onViewModeChange` to render. */
  viewMode?: "card" | "table";
  onViewModeChange?: (mode: "card" | "table") => void;
  /** Rows-per-page selector. Needs `onPageSizeChange` to render. */
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  pageSizeLabel?: string;
  /** Page-owned buttons (e.g. "New Catalog"), placed before the toggle. */
  actions?: ReactNode;
  /**
   * Page-specific extras that belong inline with the filters — a result count,
   * a tab strip, a segmented control. Rendered after the filters and before
   * the right-hand cluster, and wraps with everything else.
   */
  trailing?: ReactNode;

  className?: string;
}

/**
 * Filter bar that sits above a list, grid or DataTable.
 *
 * Composes the existing primitives — `Dropdown`, `ViewModeToggle`,
 * `RowsPerCardSelect` — so a page describes its filters as data instead of
 * hand-rolling the same markup:
 *
 * ```tsx
 * <TableToolbar
 *   search={{ value: q, onChange: setQ, onSubmit: runSearch }}
 *   filters={[
 *     { key: "type", value: type, onChange: setType, placeholder: "All Types", options: TYPES },
 *     { key: "status", value: status, onChange: setStatus, placeholder: "All Status", options: STATUSES },
 *   ]}
 * />
 * ```
 *
 * Layout: everything wraps. On a phone the search box takes the full width and
 * the filters flow underneath it; from `sm` up they share a row, with the
 * view/page-size cluster pushed to the right edge.
 */
export function TableToolbar({
  search,
  filters,
  viewMode,
  onViewModeChange,
  pageSize,
  onPageSizeChange,
  pageSizeOptions,
  pageSizeLabel,
  actions,
  trailing,
  className = "",
}: TableToolbarProps) {
  // A caller can legitimately pass undefined, and a bad payload shouldn't take
  // the toolbar down — anything that isn't an array is treated as no filters.
  const visibleFilters = (Array.isArray(filters) ? filters : []).filter(
    (f) => f && !f.hidden,
  );

  const showViewToggle = !!onViewModeChange;
  const showPageSize = !!onPageSizeChange;
  const hasRight = !!actions || showViewToggle || showPageSize;

  const submitSearch = (value: string) => search?.onSubmit?.(value.trim());

  const handleSearchChange = (value: string) => {
    search?.onChange(value);
    // Emptying the box is its own submit, so the unfiltered list comes back
    // without needing Enter.
    if (!value.trim()) submitSearch("");
  };

  return (
    // `flex-1 min-w-0` rather than `w-full`: standalone it fills its row, but
    // nested next to a sibling cluster (EntityCardGrid's view toggle + rows
    // per page) it shares the row instead of pushing it onto the next one.
    <div
      className={`flex min-w-0 flex-1 flex-wrap items-center gap-3 ${className}`.trim()}
    >
      {search && (
        // The search box leads the bar: it is the widest control and the only
        // one carrying an elevation, so the eye lands on it before the
        // filters. Filters and the right-hand cluster stay flat and secondary.
        <div className="relative w-full min-w-0 sm:w-auto sm:flex-1 sm:max-w-md">
          <button
            type="button"
            aria-label={search.ariaLabel ?? "Search"}
            disabled={search.disabled}
            onClick={() => submitSearch(search.value)}
            className="absolute left-1.5 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-lg text-[#64748b] transition-colors hover:bg-[#f1f5f9] hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-50 dark:text-white/50 dark:hover:bg-white/[0.05] dark:hover:text-indigo-400"
          >
            <Search className="size-4" />
          </button>
          <input
            type="text"
            value={search.value}
            disabled={search.disabled}
            aria-label={search.ariaLabel ?? search.placeholder ?? "Search"}
            placeholder={search.placeholder ?? "Search"}
            maxLength={search.maxLength ?? 200}
            onChange={(e) => handleSearchChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submitSearch(search.value);
              }
            }}
            className="h-[38px] w-full rounded-lg border border-border bg-card pl-10 pr-9 text-sm text-foreground shadow-sm outline-none transition-all placeholder:text-[#64748b] hover:border-indigo-400/60 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 disabled:cursor-not-allowed disabled:opacity-50 dark:placeholder:text-white/50 dark:hover:border-indigo-400/40 dark:focus:border-indigo-500"
          />
          {!!search.value && !search.disabled && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => handleSearchChange("")}
              className="absolute right-2 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-md text-[#64748b] transition-colors hover:bg-[#f1f5f9] hover:text-foreground dark:text-white/50 dark:hover:bg-white/[0.05]"
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>
      )}

      {visibleFilters.length > 0 && (
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {visibleFilters.map((filter, i) => (
            // Index-suffixed so a duplicated key can't collide.
            <Dropdown
              key={`${filter.key}-${i}`}
              size="sm"
              variant="pill"
              value={filter.value}
              onChange={filter.onChange}
              options={filter.options ?? []}
              placeholder={filter.placeholder}
              leadingLabel={filter.leadingLabel}
              searchable={filter.searchable}
              disabled={filter.disabled}
              className={filter.className ?? ""}
            />
          ))}
        </div>
      )}

      {trailing}

      {hasRight && (
        <div className="flex w-full items-center justify-end gap-3 sm:ml-auto sm:w-auto sm:flex-shrink-0">
          {actions}
          {showViewToggle && (
            <ViewModeToggle
              value={viewMode ?? "table"}
              onChange={onViewModeChange!}
            />
          )}
          {showPageSize && (
            <RowsPerCardSelect
              value={pageSize ?? 10}
              onChange={onPageSizeChange!}
              options={pageSizeOptions}
              label={pageSizeLabel}
            />
          )}
        </div>
      )}
    </div>
  );
}
